import { Box, Flex, useDisclosure } from "@chakra-ui/react"
import { useEffect, useMemo } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom"

import { ButtonWithIcon } from "@/components/Buttons/ButtonWithIcon"
import { CountTag } from "@/components/CountTag"
import { getSharedDataStatusDisplay } from "@/components/DomainTag/SharedDataStatusTag/SharedDataStatusTag"
import { BooksIcon, PlusIcon, UploadIcon } from "@/components/Icons"
import { ListToolbar } from "@/components/ListToolbar"
import { NavTab, NavTabs } from "@/components/NavTabs"
import { ReactSelect } from "@/components/ReactSelect/ReactSelect2"
import { Searchbar } from "@/components/Searchbar"
import { SEARCH_BAR_DEFAULT_MAX_WIDTH } from "@/components/Searchbar/constants"
import ShareInterBUArchivesModal from "@/components/Sharing/ShareInterBUArchivesModal"
import { ShareRequestInterbuCreateModal } from "@/components/Sharing/ShareRequestInterbuCreateModal"
import { H3, P3 } from "@/components/Typographies"
import { CollaboratorRole } from "@/domain/collaborator/constants"
import { getArchiveClusters } from "@/domain/interbu/getArchiveClusters"
import { SHARED_DATA_STATUS } from "@/domain/sharedData/constants"
import { useCurrentSearchParam } from "@/hooks/useCurrentSearchParam"
import { useGetSearchParamsState } from "@/hooks/useGetSearchParams"
import useGetUserInfos from "@/hooks/useGetUserInfos"
import useGetUserRoles from "@/hooks/useGetUserRoles"
import {
  type GetInterBuStatsParams,
  type GetUserSharedDatasParams,
  type InterBuStatsJsonldReadItemInterbuStats,
  useGetInterBuStats,
} from "@/networking/api"
import { INTER_BU_CREATE_PARAM, INTER_BU_PATHS, PATHS, SHARING_PATHS } from "@/routing/paths"

import { ExportInterBUModal } from "./components/ExportInterBUModal"
import { InterBUCardList } from "./components/InterBUCards/InterBUCardList"
import type { InterBUDirection } from "./components/InterBUCards/cardRules"
import { InterBUIndicators } from "./components/InterBUIndicators/InterBUIndicators"
import { DISTRIBUTION_STATUSES } from "./components/InterBUIndicators/indicatorRules"

const BASE_PATH = `${PATHS.SHARING}${SHARING_PATHS.INTER_BU}`

type FilterKey = { key: string; placeholderId: string; options: "agencies" | "managers" }

// Agency and manager filters of each view: the partner agency and the manager of each side; with
// both sides (all, the administrators' view), the sending and the recipient agency.
const FILTER_KEYS: Record<InterBUDirection, FilterKey[]> = {
  received: [
    {
      key: "fromCluster.id",
      placeholderId: "userSharedData.interbu.filter.agency",
      options: "agencies",
    },
    {
      key: "toManager.id",
      placeholderId: "userSharedData.interbu.filter.collaborator",
      options: "managers",
    },
  ],
  sent: [
    {
      key: "toCluster.id",
      placeholderId: "userSharedData.interbu.filter.agency",
      options: "agencies",
    },
    {
      key: "interBu.fromManager.id",
      placeholderId: "userSharedData.interbu.filter.collaborator",
      options: "managers",
    },
  ],
  all: [
    {
      key: "fromCluster.id",
      placeholderId: "userSharedData.interbu.filter.senderAgency",
      options: "agencies",
    },
    {
      key: "toCluster.id",
      placeholderId: "userSharedData.interbu.filter.recipientAgency",
      options: "agencies",
    },
  ],
}

const pendingCount = (stats?: InterBuStatsJsonldReadItemInterbuStats) =>
  stats === undefined
    ? undefined
    : ((stats.statusCounts ?? []).find(count => count.code === SHARED_DATA_STATUS.ASKED)?.count ??
      0)

// InterBU page (ADR 0009, D3): tabs "Received" then "Sent" in the URL, each with its pending
// count, a shared toolbar, and the list of cards. Perimeter, counts and filter options come from
// the API (direction filter and statistics endpoint). An administrator sees every InterBU on both
// sides: one "All" tab instead, read only (D2 revised on 2026-10-16).
export const InterBU: React.FC = () => {
  const intl = useIntl()
  const { pathname, search } = useLocation()
  const { clusterIdsByRole, data: userInfos } = useGetUserInfos()
  const { hasRoleGuru } = useGetUserRoles()
  const isAllView = hasRoleGuru()
  const direction: InterBUDirection = isAllView
    ? "all"
    : pathname.endsWith(INTER_BU_PATHS.SENT)
      ? "sent"
      : "received"
  const keys = FILTER_KEYS[direction]
  const isManager = (clusterIdsByRole[CollaboratorRole.MANAGER] ?? []).length > 0
  // Only when the API lists archives to the user: active member of an active BU.
  const hasArchives = useMemo(
    () => getArchiveClusters(userInfos?.collaborators).length > 0,
    [userInfos]
  )

  const { getSearchParams, getOrderParams, setProperty } =
    useGetSearchParamsState<GetUserSharedDatasParams>()
  const currentSearchTerm = useCurrentSearchParam()
  const params = getSearchParams as Record<string, string | undefined>

  // Only the keys of the current direction reach the list and the statistics.
  const filters = useMemo(() => {
    const tabFilters: Record<string, string | undefined> = {
      search: params.search,
      "status.code": params["status.code"],
      ...Object.fromEntries(keys.map(({ key }) => [key, params[key]])),
    }
    return { ...tabFilters, ...getOrderParams } as GetUserSharedDatasParams
  }, [params, keys, getOrderParams])

  // Both tabs' counts, whatever the filters; the current tab also gives the filter options. The
  // administrators' single view has its own.
  const receivedStats = useGetInterBuStats({ direction: "received" } as GetInterBuStatsParams, {
    query: { enabled: !isAllView },
  })
  const sentStats = useGetInterBuStats({ direction: "sent" } as GetInterBuStatsParams, {
    query: { enabled: !isAllView },
  })
  const allStats = useGetInterBuStats({ direction: "all" } as GetInterBuStatsParams, {
    query: { enabled: isAllView },
  })
  const currentStats = {
    received: receivedStats.data,
    sent: sentStats.data,
    all: allStats.data,
  }[direction]

  const agencyOptions = useMemo(
    () =>
      (currentStats?.agencyOptions ?? []).map(o => ({ value: o.id ?? "", label: o.label ?? "" })),
    [currentStats]
  )
  const managerOptions = useMemo(
    () =>
      (currentStats?.managerOptions ?? []).map(o => ({ value: o.id ?? "", label: o.label ?? "" })),
    [currentStats]
  )

  // Same statuses and labels as the status pills and the distribution tile (ADR 0009, D1).
  const statusOptions = useMemo(
    () =>
      DISTRIBUTION_STATUSES.map(code => ({
        value: code,
        label: intl.formatMessage({ id: getSharedDataStatusDisplay(code).messageId }),
      })),
    [intl]
  )

  const hasInterBu = isAllView
    ? (allStats.data?.total ?? 0) > 0
    : (receivedStats.data?.total ?? 0) + (sentStats.data?.total ?? 0) > 0
  const canExport = (hasRoleGuru() || isManager) && hasInterBu

  const create = useDisclosure()
  const archives = useDisclosure()
  const exportModal = useDisclosure()

  // ?create=1 opens the creation, e.g. from the dashboard (ADR 0013, D3), then leaves the address
  // so that a reload or the back button does not open it again.
  const [searchParams, setSearchParams] = useSearchParams()
  const { onOpen: openCreation } = create
  useEffect(() => {
    if (searchParams.get(INTER_BU_CREATE_PARAM) !== "1") return
    openCreation()
    const next = new URLSearchParams(searchParams)
    next.delete(INTER_BU_CREATE_PARAM)
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams, openCreation])

  const filterSelect = (
    key: string,
    options: { value: string; label: string }[],
    placeholderId: string,
    isLoading = false
  ) => (
    <Box w={{ base: "full", md: "200px" }} flexShrink={0}>
      <ReactSelect
        options={options}
        value={options.find(option => option.value === params[key])}
        onChange={nv => setProperty(key as Parameters<typeof setProperty>[0], nv?.value)}
        isLoading={isLoading}
        variant="outline"
        style={{ variant: "headFilter" }}
        isClearable={true}
        placeholder={intl.formatMessage({ id: placeholderId })}
      />
    </Box>
  )

  return (
    <Flex flexDir="column" gap="6" px={{ base: 0, lg: 8 }} py={{ base: 2, lg: 12 }}>
      {/* Two rows: title and page actions, then search and filters (the search needs room). */}
      <Flex alignItems="center" justifyContent="space-between" gap="4" wrap="wrap">
        <Box>
          <H3>
            <FormattedMessage id="sharing.interbu.title" />
          </H3>
          <P3 color="textMuted">
            <FormattedMessage id="sharing.interbu.intro" />
          </P3>
        </Box>
        <Flex gap="2" wrap="wrap">
          {canExport && (
            <ButtonWithIcon
              variant="outline"
              size="toolbar"
              icon={UploadIcon}
              onClick={exportModal.onOpen}
            >
              <FormattedMessage id="userSharedData.interbu.exportAll" />
            </ButtonWithIcon>
          )}
          {hasArchives && (
            <ButtonWithIcon
              variant="outline"
              size="toolbar"
              icon={BooksIcon}
              onClick={archives.onOpen}
            >
              <FormattedMessage id="userSharedData.interbu.archives" />
            </ButtonWithIcon>
          )}
          <ButtonWithIcon variant="accent" size="toolbar" icon={PlusIcon} onClick={create.onOpen}>
            <FormattedMessage id="userSharedData.interbu.add" />
          </ButtonWithIcon>
        </Flex>
      </Flex>

      {/* Indicators first, above the list and its toolbar (ADR 0009, D1). */}
      <InterBUIndicators
        stats={currentStats}
        direction={direction}
        activeStatus={params["status.code"]}
        onStatusFilter={code => setProperty("status.code", code)}
      />

      <ListToolbar
        search={
          <Searchbar
            searchbarPlaceholder={intl.formatMessage({ id: "sharing.search.placeholder_by_uuid" })}
            submitButtonAriaLabel={intl.formatMessage({ id: "sharing.search.button.ariaLabel" })}
            maxW={SEARCH_BAR_DEFAULT_MAX_WIDTH}
            minW="240px"
            flexGrow={1}
          />
        }
        filters={
          <>
            {filterSelect("status.code", statusOptions, "userSharedData.interbu.filter.status")}
            {keys.map(({ key, placeholderId, options }) =>
              filterSelect(
                key,
                options === "agencies" ? agencyOptions : managerOptions,
                placeholderId
              )
            )}
          </>
        }
      />

      {isAllView ? (
        <NavTabs>
          <NavTab exact to={`${BASE_PATH}${INTER_BU_PATHS.ALL}`}>
            <FormattedMessage id="sharing.interbu.tabs.all" />
            {/* The administrator does not answer: the total, not the pending ones (user feedback). */}
            <PendingCount count={allStats.data?.total} variant="round" />
          </NavTab>
        </NavTabs>
      ) : (
        <NavTabs>
          <NavTab exact to={`${BASE_PATH}${INTER_BU_PATHS.RECEIVED}`}>
            <FormattedMessage id="sharing.interbu.tabs.received" />
            {/* Pending received shares: an answer is required, hence the red counter. */}
            <PendingCount count={pendingCount(receivedStats.data)} variant="counterAlert" />
          </NavTab>
          <NavTab exact to={`${BASE_PATH}${INTER_BU_PATHS.SENT}`}>
            <FormattedMessage id="sharing.interbu.tabs.sent" />
            {/* Pending sent shares wait for the other agency: neutral counter. */}
            <PendingCount count={pendingCount(sentStats.data)} variant="round" />
          </NavTab>
        </NavTabs>
      )}

      {isAllView ? (
        <Routes>
          <Route
            path={INTER_BU_PATHS.ALL}
            element={
              <InterBUCardList
                direction="all"
                filters={filters}
                currentSearchTerm={currentSearchTerm}
              />
            }
          />
          {/* Received, sent or nothing: the single view, keeping ?create=1 (dashboard link). */}
          <Route
            path="*"
            element={
              <Navigate to={{ pathname: `${BASE_PATH}${INTER_BU_PATHS.ALL}`, search }} replace />
            }
          />
        </Routes>
      ) : (
        <Routes>
          <Route
            index
            element={<Navigate to={`${BASE_PATH}${INTER_BU_PATHS.RECEIVED}`} replace />}
          />
          <Route
            path={INTER_BU_PATHS.RECEIVED}
            element={
              <InterBUCardList
                direction="received"
                filters={filters}
                currentSearchTerm={currentSearchTerm}
              />
            }
          />
          <Route
            path={INTER_BU_PATHS.SENT}
            element={
              <InterBUCardList
                direction="sent"
                filters={filters}
                currentSearchTerm={currentSearchTerm}
              />
            }
          />
          <Route
            path={INTER_BU_PATHS.ALL}
            element={<Navigate to={`${BASE_PATH}${INTER_BU_PATHS.RECEIVED}`} replace />}
          />
        </Routes>
      )}

      <ShareInterBUArchivesModal isOpen={archives.isOpen} onClose={archives.onClose} />
      <ExportInterBUModal isOpen={exportModal.isOpen} onClose={exportModal.onClose} />
      <ShareRequestInterbuCreateModal isOpen={create.isOpen} onClose={create.onClose} />
    </Flex>
  )
}

// Hidden at 0: a counter only signals something to look at.
const PendingCount: React.FC<{ count?: number; variant: string }> = ({ count, variant }) =>
  count !== undefined && count > 0 ? (
    <CountTag count={count} variant={variant} hideParentheses />
  ) : null
